/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    AfterViewInit,
    Component,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { customTemplates, templatesConfiguration } from 'ng/modules/logs/constants/logs-signpost.constants';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { FilterOperatorType, ILogsDefaultSignpostData } from '../../../../logs.types';
import { TrafficLogsSignpostStore } from '../../../traffic-logs/traffic-logs-sidebar/traffic-logs-signpost.store';
import { getPolicyRequestParams, getSslRequestParams } from '../../../../utils/logs-signpost.utils';
import './logs-signpost-custom-groups.component.less';
import * as l10n from './logs-signpost-custom-groups.l10n';
import {
    ITrafficLogsStates,
    IVsConfigForTrafficLogs,
    TrafficLogsStore,
} from '../../../traffic-logs/traffic-logs.store';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Signpost component for policy and ssl section.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-custom-groups',
    templateUrl: './logs-signpost-custom-groups.component.html',
})
export class LogsSignpostCustomGroupsComponent implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Selected signpost config key.
     */
    @Input()
    public configKey: string;

    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    /**
     * Datagrid template for column name.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for column progress bar.
     */
    @ViewChild('progressBarFieldTemplateRef')
    public readonly progressBarFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Hold the virtual service configuration settings for traffic logs.
     */
    public vs: IVsConfigForTrafficLogs;

    public groupsGridConfig: IAviDataGridConfig;

    public readonly FilterOperatorType = FilterOperatorType;

    public readonly l10nKeys = l10nKeys;

    private readonly destroy$ = new Subject<void>();

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        private readonly l10nService: L10nService,
        public readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.trafficLogsStore.vsConfig$.pipe(
            takeUntil(this.destroy$),
        ).subscribe(vsConfig => {
            this.vs = vsConfig;

            const fullSignpostParams = this.configKey === customTemplates.policy ?
                getPolicyRequestParams(this.vs) : getSslRequestParams();

            fullSignpostParams.forEach(param => {
                addPoolIdFilterParams(param.requestParams, this.poolId);
            });

            this.trafficLogsSignpostStore.getLogSignpostCombinedData(fullSignpostParams);
        });
    }

    /** @override */
    public ngAfterViewInit(): void {
        const {
            l10nService,
            configKey,
        } = this;

        this.groupsGridConfig = {
            isGridExpandable: true,
            layout: {
                expandGridByDefault: true,
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'name',
                    templateRef: this.nameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(
                        l10nKeys.logsNumberLabel,
                    ),
                    id: 'logs',
                    transform: (row: ILogsDefaultSignpostData): number => row.count,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsPercentageLabel),
                    id: 'percentage',
                    transform: (row: ILogsDefaultSignpostData): string => `${row.percentage}%`,
                },
                {
                    label: '',
                    id: 'progress-bar',
                    templateRef: this.progressBarFieldTemplateRef,
                },
            ],
        };
    }

    public trackbyIndex(index: number): number {
        return index;
    }

    /** @override */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
